/**
 * Fleet Booking Slice
 *
 * State for the fleet portal's booking calendar (CLIENT users):
 * - Calendar for a month: own bookings (full detail) + occupied slots (anonymous)
 * - Create a booking
 * - Cancel a booking
 *
 * Everything is scoped server-side by the customerUuid JWT claim.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    fetchFleetCalendarApi,
    fetchFleetServiceCategoriesApi,
    fetchFleetServiceProfilesApi,
    createFleetBookingApi,
    cancelFleetBookingApi,
} from '../../services/fleetBookingMgtApi.js';

// ============================================
// Async Thunks
// ============================================

export const fetchFleetCalendar = createAsyncThunk(
    'fleetBookings/fetchFleetCalendar',
    async ({ startDate, endDate }, { rejectWithValue }) => {
        try {
            const data = await fetchFleetCalendarApi({ startDate, endDate });
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load your calendar.';
            return rejectWithValue(message);
        }
    }
);

export const fetchFleetServiceCategories = createAsyncThunk(
    'fleetBookings/fetchFleetServiceCategories',
    async (_, { rejectWithValue }) => {
        try {
            return await fetchFleetServiceCategoriesApi();
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load service types.';
            return rejectWithValue(message);
        }
    }
);

export const fetchFleetServiceProfiles = createAsyncThunk(
    'fleetBookings/fetchFleetServiceProfiles',
    async (_, { rejectWithValue }) => {
        try {
            return await fetchFleetServiceProfilesApi();
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load service packages.';
            return rejectWithValue(message);
        }
    }
);

export const createFleetBooking = createAsyncThunk(
    'fleetBookings/createFleetBooking',
    async (payload, { rejectWithValue }) => {
        try {
            return await createFleetBookingApi(payload);
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to create the booking.';
            return rejectWithValue(message);
        }
    }
);

export const cancelFleetBooking = createAsyncThunk(
    'fleetBookings/cancelFleetBooking',
    async (bookingUuid, { rejectWithValue }) => {
        try {
            await cancelFleetBookingApi(bookingUuid);
            return bookingUuid;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to cancel the booking.';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    // Calendar — myBookings carry full detail; occupiedSlots are anonymous
    // (date/time/duration only) blocks for other customers' bookings.
    myBookings: [],
    occupiedSlots: [],
    loading: false,
    error: null,

    // Service categories — the workshop's offered services, for the booking
    // form dropdown. `value` is the enum name the backend expects back.
    serviceCategories: [],
    serviceCategoriesLoading: false,

    // Service packages — the workshop's ServiceProfiles, for the booking form.
    serviceProfiles: [],
    serviceProfilesLoading: false,

    // Create
    creating: false,
    createError: null,

    // Cancel
    cancellingUuid: null,
    cancelError: null,
};

const fleetBookingSlice = createSlice({
    name: 'fleetBookings',
    initialState,
    reducers: {
        clearFleetBookingErrors(state) {
            state.error = null;
            state.createError = null;
            state.cancelError = null;
        },
    },
    extraReducers: (builder) => {
        // ---- Calendar ----
        builder
            .addCase(fetchFleetCalendar.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFleetCalendar.fulfilled, (state, action) => {
                state.loading = false;
                state.myBookings = action.payload.myBookings || [];
                state.occupiedSlots = action.payload.occupiedSlots || [];
            })
            .addCase(fetchFleetCalendar.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load your calendar.';
            });

        // ---- Service categories ----
        builder
            .addCase(fetchFleetServiceCategories.pending, (state) => {
                state.serviceCategoriesLoading = true;
            })
            .addCase(fetchFleetServiceCategories.fulfilled, (state, action) => {
                state.serviceCategoriesLoading = false;
                state.serviceCategories = action.payload || [];
            })
            .addCase(fetchFleetServiceCategories.rejected, (state) => {
                state.serviceCategoriesLoading = false;
                state.serviceCategories = [];
            });

        // ---- Service profiles ----
        builder
            .addCase(fetchFleetServiceProfiles.pending, (state) => {
                state.serviceProfilesLoading = true;
            })
            .addCase(fetchFleetServiceProfiles.fulfilled, (state, action) => {
                state.serviceProfilesLoading = false;
                state.serviceProfiles = action.payload || [];
            })
            .addCase(fetchFleetServiceProfiles.rejected, (state) => {
                state.serviceProfilesLoading = false;
                state.serviceProfiles = [];
            });

        // ---- Create ----
        builder
            .addCase(createFleetBooking.pending, (state) => {
                state.creating = true;
                state.createError = null;
            })
            .addCase(createFleetBooking.fulfilled, (state) => {
                state.creating = false;
            })
            .addCase(createFleetBooking.rejected, (state, action) => {
                state.creating = false;
                state.createError = action.payload || 'Failed to create the booking.';
            });

        // ---- Cancel ----
        builder
            .addCase(cancelFleetBooking.pending, (state, action) => {
                state.cancellingUuid = action.meta.arg;
                state.cancelError = null;
            })
            .addCase(cancelFleetBooking.fulfilled, (state) => {
                state.cancellingUuid = null;
            })
            .addCase(cancelFleetBooking.rejected, (state, action) => {
                state.cancellingUuid = null;
                state.cancelError = action.payload || 'Failed to cancel the booking.';
            });
    },
});

export const { clearFleetBookingErrors } = fleetBookingSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectFleetMyBookings = (state) => state.fleetBookings.myBookings;
export const selectFleetOccupiedSlots = (state) => state.fleetBookings.occupiedSlots;
export const selectFleetCalendarLoading = (state) => state.fleetBookings.loading;
export const selectFleetCalendarError = (state) => state.fleetBookings.error;
export const selectFleetBookingCreating = (state) => state.fleetBookings.creating;
export const selectFleetBookingCreateError = (state) => state.fleetBookings.createError;
export const selectFleetBookingCancellingUuid = (state) => state.fleetBookings.cancellingUuid;
export const selectFleetBookingCancelError = (state) => state.fleetBookings.cancelError;
export const selectFleetServiceCategories = (state) => state.fleetBookings.serviceCategories;
export const selectFleetServiceCategoriesLoading = (state) => state.fleetBookings.serviceCategoriesLoading;
export const selectFleetServiceProfiles = (state) => state.fleetBookings.serviceProfiles;
export const selectFleetServiceProfilesLoading = (state) => state.fleetBookings.serviceProfilesLoading;

export default fleetBookingSlice.reducer;